import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Article, ArticleSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { HeroTitle } from '@/components/generals/hero-title'
import { RichText } from '@/components/generals/rich-text'
import { SectionLabel } from '@/components/generals/section-label'
import { Wordmark } from '@/components/generals/wordmark'
import ArticleCard from '@/components/sections/article-card'
import FinalCta from '@/components/sections/final-cta'

import {
    REVALIDATE_SECONDS,
    getArticle,
    getArticles,
    getSlugs
} from '@/lib/content'

interface ArticlePageProps {
    article: Article
    related: ArticleSummary[]
    previous: Pick<ArticleSummary, 'slug' | 'title'> | null
    next: Pick<ArticleSummary, 'slug' | 'title'> | null
}

const ArticlePage: FC<ArticlePageProps> = ({ article, related, previous, next }): ReactNode => {
    const author = article.author ?? { name: 'BLITZ Editorial', role: 'Strategy, creative and performance team' }

    return (
        <Layout>
            <Seo title={article.title} description={article.excerpt} ogImage={article.cover ?? undefined} />

            <article>
                <header data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 pb-12 pt-32 md:px-10 md:pb-16 md:pt-40'>
                        <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-4 pb-5'>
                            <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-charcoal/20' style={revealDelay(100)} />
                            <Link href='/insights' className='reveal-fade group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/80 transition-colors hover:text-charcoal'>
                                <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-1' />
                                All insights
                            </Link>
                            <p className='reveal-fade font-heading text-base font-extrabold tracking-[-0.03em]' style={revealDelay(150)}>The BLITZ journal</p>
                        </div>

                        <div className='mx-auto mt-12 flex max-w-4xl flex-col items-center text-center md:mt-16'>
                            <SectionLabel className='reveal-fade justify-center'>{article.category || 'Insight'}</SectionLabel>
                            <HeroTitle
                                className='mt-6 text-[clamp(2.1rem,4.2vw,4rem)] font-extrabold leading-[1.03] tracking-[-0.04em] text-balance'
                                delay={200}
                                lines={[article.title]}
                            />
                            {article.excerpt && (
                                <p className='reveal-fade mt-6 max-w-2xl text-lg leading-relaxed text-charcoal/85 md:mt-8 md:text-xl' style={revealDelay(380)}>{article.excerpt}</p>
                            )}

                            <dl className='reveal-fade mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[14px]' style={revealDelay(480)}>
                                <div className='flex items-center gap-2'>
                                    <dt className='sr-only'>Written by</dt>
                                    <dd className='font-semibold'>{author.name}</dd>
                                </div>
                                {article.publishedAt && (
                                    <div className='flex items-center gap-2'>
                                        <dt className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>Published</dt>
                                        <dd className='font-semibold'>
                                            <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
                                        </dd>
                                    </div>
                                )}
                                {article.readingTime ? (
                                    <div className='flex items-center gap-2'>
                                        <dt className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>Reading time</dt>
                                        <dd className='font-semibold'>{article.readingTime} minutes</dd>
                                    </div>
                                ) : null}
                            </dl>
                        </div>
                    </div>

                    {article.cover && (
                        <div className='mx-auto max-w-[1440px] px-5 md:px-10'>
                            <figure className='relative'>
                                <div aria-hidden='true' className='reveal-fade absolute -right-3 -top-3 h-1/2 w-1/3 border border-charcoal md:-right-5 md:-top-5' style={revealDelay(700)} />
                                <div className='reveal-clip relative aspect-[4/3] overflow-hidden bg-stone sm:aspect-[16/9] lg:aspect-[21/9]' style={revealDelay(520)}>
                                    <Image src={article.cover} alt={article.coverAlt} fill priority sizes='100vw' className='object-cover' />
                                </div>
                            </figure>
                        </div>
                    )}
                </header>

                <div data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:px-10 md:py-24 lg:grid-cols-12 lg:gap-10'>
                        <aside className='lg:col-span-3'>
                            <div className='flex items-center gap-4 border-t border-charcoal pt-5 lg:sticky lg:top-28'>
                                <span aria-hidden='true' className='flex size-12 shrink-0 items-center justify-center rounded-sm bg-charcoal text-cream'>
                                    <Wordmark className='text-[0.8rem]' />
                                </span>
                                <div>
                                    <p className='text-[15px] font-semibold'>{author.name}</p>
                                    <p className='text-[13px] text-charcoal/80'>{author.role}</p>
                                </div>
                            </div>
                        </aside>

                        <RichText html={article.body} className='max-w-[68ch] text-[17px] leading-[1.75] text-charcoal lg:col-span-8 lg:col-start-5' />
                    </div>

                    {(previous || next) && (
                        <nav aria-label='More articles' className='mx-auto grid max-w-[1440px] gap-px border-t border-charcoal/20 px-5 md:grid-cols-2 md:px-10'>
                            {previous ? (
                                <Link href={`/insights/${previous.slug}`} className='group flex flex-col gap-3 py-10 md:pr-10'>
                                    <span className='inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'>
                                        <ArrowLeft aria-hidden='true' className='size-4 transition-transform group-hover:-translate-x-1' />
                                        Previous article
                                    </span>
                                    <span className='font-heading text-xl font-extrabold leading-tight tracking-[-0.025em] underline-offset-4 group-hover:underline md:text-2xl'>{previous.title}</span>
                                </Link>
                            ) : <span />}
                            {next && (
                                <Link href={`/insights/${next.slug}`} className='group flex flex-col items-start gap-3 border-t border-charcoal/20 py-10 md:items-end md:border-l md:border-t-0 md:pl-10 md:text-right'>
                                    <span className='inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'>
                                        Next article
                                        <ArrowRight aria-hidden='true' className='size-4 transition-transform group-hover:translate-x-1' />
                                    </span>
                                    <span className='font-heading text-xl font-extrabold leading-tight tracking-[-0.025em] underline-offset-4 group-hover:underline md:text-2xl'>{next.title}</span>
                                </Link>
                            )}
                        </nav>
                    )}
                </div>
            </article>

            {related.length > 0 && (
                <section aria-labelledby='related-articles-title' data-nav-theme='light' className='bg-coral text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <SectionLabel className='text-charcoal'>Related insights</SectionLabel>
                        <h2 id='related-articles-title' className='mt-6 font-heading type-section'>
                            Keep reading.
                        </h2>
                        <div className='mt-14 grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3'>
                            {related.map((item) => (
                                <ArticleCard key={item.id} article={item} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <FinalCta />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getSlugs('articles')

    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ArticlePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const article = slug ? await getArticle(slug) : null

    if (!article) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const articles = await getArticles()
    const index = articles.findIndex((item) => item.slug === slug)
    const others = articles.filter((item) => item.slug !== slug)
    const sameCategory = others.filter((item) => item.category === article.category)
    const related = [...sameCategory, ...others.filter((item) => item.category !== article.category)].slice(0, 3)
    const newer = index > 0 ? articles[index - 1] : null
    const older = index >= 0 && index < articles.length - 1 ? articles[index + 1] : null

    return {
        props: {
            article,
            related,
            previous: older ? { slug: older.slug, title: older.title } : null,
            next: newer ? { slug: newer.slug, title: newer.title } : null
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default ArticlePage
